import { Stack } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { getPolicyColor } from 'src/components/Assistant/policies-tab/policy-color';
import { sortPoliciesForDisplay } from 'src/components/Assistant/policies-tab/policy-order';
import { useApplicablePolicies } from 'src/components/Assistant/policies-tab/use-applicable-policies';
import { useSidekickTransition } from 'src/components/Navbar/DesktopDrawer/SidekickPane/use-sidekick-transition';
import { useScrollEdges } from 'src/components/Navbar/DesktopDrawer/use-scroll-edges';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useGroupRules, UserGroupRules } from 'src/graphql';
import { useGlobalStore } from 'src/store';
import useNavigationStore from 'src/store/navigation';
import { getDisplayedTextFromHtml } from 'src/utils/text-parsing';
import {
  getRowTintSx,
  getStageSx,
  PolicyRow,
  PreviewCard,
  PreviewLabel,
  RowBody,
  RowDesc,
  RowDot,
  RowName,
  RowRail,
  RowTitle,
  ScrollCue,
} from './active-policies-preview.styles';

const SCROLL_EDGE_TOLERANCE_PX = 2;
const SCROLL_CUE_OVERLAP_PX = 28;

/** A launcher, not a second surface: a row opens the full Policies panel. */
export const ActivePoliciesPreview = () => {
  const { checkValidity, loading: rulesLoading } = useGroupRules();
  const currentPartnerId = useGlobalStore.use.selectedPartnerId();
  const setActivePane = useNavigationStore((state) => state.setActivePane);
  const requestSidekick = useNavigationStore((state) => state.requestSidekick);
  const { hasTransitioned, completeTransition } = useSidekickTransition();

  const canSeePreview = !rulesLoading && checkValidity([UserGroupRules.AccessToPolicyPreview]);

  const { applicablePolicies } = useApplicablePolicies({
    fallbackPartnerId: currentPartnerId ?? PLATFORM_PARTNER_ID,
  });

  const rows = useMemo(
    () =>
      sortPoliciesForDisplay(applicablePolicies).map((policy) => ({
        id: policy.id,
        title: policy.title,
        summary: getDisplayedTextFromHtml(policy.description),
        color: getPolicyColor(policy),
      })),
    [applicablePolicies],
  );

  const {
    ref: stageRef,
    edges,
    syncEdges,
  } = useScrollEdges<HTMLDivElement>({
    axis: 'vertical',
    tolerancePx: SCROLL_EDGE_TOLERANCE_PX,
    resyncOn: rows.length,
  });

  const openPolicy = useCallback(
    (policyId: string) => {
      requestSidekick({ subtab: 'policies', policyId });
      // else the old bottom bar keeps sitting under the pane it just opened
      if (!hasTransitioned) {
        completeTransition();
      }
      setActivePane('sidekick');
    },
    [requestSidekick, hasTransitioned, completeTransition, setActivePane],
  );

  if (!canSeePreview || rows.length === 0) {
    return null;
  }

  return (
    <PreviewCard data-testid="active-policies-preview">
      <PreviewLabel>Active policies</PreviewLabel>

      <Stack ref={stageRef} onScroll={syncEdges} sx={getStageSx(edges.start, edges.end)}>
        {rows.map((row) => (
          <PolicyRow
            key={row.id}
            data-testid="active-policies-preview-row"
            data-policy-id={row.id}
            type="button"
            onClick={() => openPolicy(row.id)}
            sx={getRowTintSx(row.color)}
          >
            <RowRail sx={{ backgroundColor: row.color }} />
            <RowBody>
              <RowName>
                <RowDot sx={{ backgroundColor: row.color }} />
                <RowTitle>{row.title}</RowTitle>
              </RowName>
              {row.summary ? <RowDesc>{row.summary}</RowDesc> : null}
            </RowBody>
          </PolicyRow>
        ))}
      </Stack>

      {edges.end ? (
        <ScrollCue
          type="button"
          aria-label="Scroll for more policies"
          onClick={() =>
            stageRef.current?.scrollBy({
              top: stageRef.current.clientHeight - SCROLL_CUE_OVERLAP_PX,
              behavior: 'smooth',
            })
          }
        >
          <ChevronDown size={15} />
        </ScrollCue>
      ) : null}
    </PreviewCard>
  );
};
